import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Field } from "@/components/ds";
import { ImageUpload } from "@/components/ds/ImageUpload";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

export function DevConvertModal({ project, cats = [], onClose, onSaved }) {
  const unitCost = Number(project.cost?.unit || 0);
  const [preview, setPreview] = useState(null);
  const [transferStock, setTransferStock] = useState(true);
  const [form, setForm] = useState({
    name: project.name,
    category_id: project.category_id || "",
    description: project.description || "",
    photo_path: project.photo_path || null,
    cost: unitCost ? unitCost.toFixed(2) : "0",
    price: unitCost ? (unitCost * 3).toFixed(2) : "0",
    consign_price: unitCost ? (unitCost * 2).toFixed(2) : "0",
    reseller_price: unitCost ? (unitCost * 1.8).toFixed(2) : "0",
    show_in_catalog: true,
    kit_eligible: true,
  });
  // As cores do projeto formam UMA única combinação comercial (1 a 4 cores),
  // preservando a ordem definida no Desenvolvimento.
  const [variations, setVariations] = useState(() => {
    const colors = (project.filaments || []).map((f) => ({
      name: f.color_name,
      filament_id: f.id,
    }));
    if (colors.length === 0) return [];
    return [
      { selected: true, name: colors.map((c) => c.name).join(" + "), colors },
    ];
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api
      .get(`/dev-projects/${project.id}/stock-preview`)
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview({ good: 0 }));
  }, [project.id]);

  const selectedVariations = variations.filter((v) => v.selected && v.name.trim());
  const [stockVariation, setStockVariation] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/dev-projects/${project.id}/convert`, {
        name: form.name,
        category_id: form.category_id || null,
        category: "",
        description: form.description,
        photo_path: form.photo_path,
        cost: Number(form.cost || 0),
        price: Number(form.price || 0),
        consign_price: Number(form.consign_price || 0),
        reseller_price: Number(form.reseller_price || 0),
        show_in_catalog: form.show_in_catalog,
        kit_eligible: form.kit_eligible,
        variations: variations
          .filter((v) => v.selected && v.name.trim())
          .map((v) => ({ name: v.name, colors: v.colors })),
        transfer_stock: transferStock,
        stock_variation_name: stockVariation || selectedVariations[0]?.name || null,
      });
      onClose();
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Transformar em produto"
      description="Os dados vêm do projeto. Confirme ou ajuste antes de criar o produto no módulo Produtos."
      testid="dev-convert-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <ImageUpload
          value={form.photo_path}
          onChange={(p) => setForm((f) => ({ ...f, photo_path: p }))}
          testid="convert-image"
        />
        <Field label="Nome do produto">
          <Input value={form.name} onChange={set("name")} required data-testid="convert-name-input" />
        </Field>
        <Field label="Categoria">
          <NativeSelect
            value={form.category_id}
            onChange={(v) => setForm((f) => ({ ...f, category_id: v }))}
            options={[
              { value: "", label: "Sem categoria" },
              ...cats.map((c) => ({ value: c.id, label: c.name })),
            ]}
            data-testid="convert-category-select"
          />
        </Field>
        <Field label="Descrição">
          <Textarea value={form.description} onChange={set("description")} rows={2} data-testid="convert-description-input" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Custo por unidade (R$)" hint={unitCost ? `Calculado: ${brl(unitCost)}` : ""}>
            <Input type="number" min="0" step="0.01" value={form.cost} onChange={set("cost")} className="font-num" data-testid="convert-cost-input" />
          </Field>
          <Field label="Preço público (R$)">
            <Input type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="font-num" data-testid="convert-price-input" />
          </Field>
          <Field label="Preço consignado (R$)">
            <Input type="number" min="0" step="0.01" value={form.consign_price} onChange={set("consign_price")} className="font-num" data-testid="convert-consign-input" />
          </Field>
          <Field label="Preço lojista (R$)">
            <Input type="number" min="0" step="0.01" value={form.reseller_price} onChange={set("reseller_price")} className="font-num" data-testid="convert-reseller-input" />
          </Field>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              As cores do projeto formam uma única combinação
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setVariations((list) => [...list, { selected: true, name: "", colors: [] }])
              }
              data-testid="convert-add-variation-btn"
              className="h-8 gap-1.5 rounded-full text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Adicionar
            </Button>
          </div>
          {variations.length === 0 ? (
            <p className="rounded-lg border border-dashed border-white/15 bg-secondary/30 p-3 text-xs text-muted-foreground">
              Sem combinações: o produto será criado sem cores, como qualquer produto simples.
            </p>
          ) : (
            <div className="space-y-2">
              {variations.map((v, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2"
                  data-testid={`convert-variation-${idx}`}
                >
                  <Switch
                    checked={v.selected}
                    onCheckedChange={(val) =>
                      setVariations((list) =>
                        list.map((x, k) => (k === idx ? { ...x, selected: val } : x))
                      )
                    }
                    data-testid={`convert-variation-switch-${idx}`}
                  />
                  <Input
                    value={v.name}
                    onChange={(e) =>
                      setVariations((list) =>
                        list.map((x, k) => (k === idx ? { ...x, name: e.target.value } : x))
                      )
                    }
                    placeholder="Nome da combinação"
                    className="h-9 flex-1"
                    data-testid={`convert-variation-name-${idx}`}
                  />
                  <span
                    className="hidden shrink-0 text-[11px] text-muted-foreground sm:block"
                    data-testid={`convert-variation-colors-${idx}`}
                  >
                    {v.colors.length} cor(es)
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setVariations((list) => list.filter((_, k) => k !== idx))}
                    className="h-8 px-2 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5">
            <span className="text-sm">Mostrar no catálogo</span>
            <Switch
              checked={form.show_in_catalog}
              onCheckedChange={(v) => setForm((f) => ({ ...f, show_in_catalog: v }))}
              data-testid="convert-catalog-switch"
            />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5">
            <span className="text-sm">Pode entrar em kit</span>
            <Switch
              checked={form.kit_eligible}
              onCheckedChange={(v) => setForm((f) => ({ ...f, kit_eligible: v }))}
              data-testid="convert-kit-switch"
            />
          </label>
        </div>

        {preview?.good > 0 && !preview.already_transferred && (
          <div
            className="space-y-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4"
            data-testid="convert-stock-block"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-sm">Peças boas do último teste aprovado</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Teste #{preview.attempt} · {preview.produced} un produzidas ·{" "}
                  <strong className="font-num text-emerald-300">{preview.good} un boas</strong>
                  {preview.plates?.length > 1
                    ? ` · ${preview.plates.length} Plates (limite pela menor)`
                    : ""}
                </p>
              </div>
              <Switch
                checked={transferStock}
                onCheckedChange={setTransferStock}
                data-testid="convert-transfer-switch"
              />
            </div>
            {transferStock && (
              <>
                <p className="text-xs">
                  <strong className="font-num">{preview.good}</strong> un entram no estoque do
                  produto. As peças já existem e o filamento já foi consumido na produção — nada é
                  descontado de novo.
                </p>
                {selectedVariations.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-[11px] text-muted-foreground">
                      Combinação que recebe as unidades
                    </p>
                    <NativeSelect
                      value={stockVariation || selectedVariations[0].name}
                      onChange={setStockVariation}
                      options={selectedVariations.map((v) => ({ value: v.name, label: v.name }))}
                      data-testid="convert-stock-variation-select"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {preview?.already_transferred && (
          <p className="rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground">
            As unidades boas deste projeto já foram adicionadas ao estoque anteriormente — não serão
            somadas de novo.
          </p>
        )}

        <p className="text-[11px] text-muted-foreground">
          As Plates continuam vinculadas ao projeto e o histórico de desenvolvimento permanece salvo.
        </p>
        <Button
          type="submit"
          disabled={busy}
          data-testid="save-convert-btn"
          className="h-12 rounded-full font-semibold transition-transform active:scale-95"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Criar produto
        </Button>
      </form>
    </ResponsiveModal>
  );
}
